'use client'

import { useAppError } from '@/components/error-page/errors'
import { type AuthGmQuery, useAuthGmQuery } from '@/generated/graphql'
import { isUnauthenticatedApolloError } from '@/helpers/apollo/ApolloClient'
import { getGraphQLErrors } from '@/helpers/apollo/errors'

import React, { type PropsWithChildren, useMemo } from 'react'
import { match, P } from 'ts-pattern'

type LoadingAuthState = { type: 'loading' }
export type LoadedAuthState =
  | { type: 'authenticated'; user: AuthGmQuery['me'] }
  | { type: 'unauthenticated'; reload: () => void }
export type AuthState = LoadingAuthState | LoadedAuthState

const AuthStateContext = React.createContext<AuthState | null>(null)

export const AuthStateProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { loading, error, data, refetch } = useAuthGmQuery()

  const state = useMemo<AuthState>(() => {
    const unauthenticated = (): AuthState => ({
      type: 'unauthenticated',
      reload: () => {
        void refetch()
      },
    })

    return match({ loading, error, data })
      .with({ data: { me: { account: P.not(P.nullish) } } }, ({ data: { me } }): AuthState => ({
        type: 'authenticated',
        user: me,
      }))
      .with({ loading: true }, (): AuthState => ({ type: 'loading' }))
      .when(({ error: err }) => isUnauthenticatedApolloError(err), unauthenticated)
      .when(({ error: err }) => Boolean(err) && getGraphQLErrors(err).length === 0, unauthenticated)
      .with({ error: P.not(P.nullish) }, ({ error: err }) => {
        throw err
      })
      .otherwise(unauthenticated)
  }, [loading, error, data, refetch])

  return <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>
}

// For cross-cutting hooks (feature flags) that also render in trees without the provider.
export const useOptionalAuthState = (): AuthState | null => React.useContext(AuthStateContext)

export const useAuthState = (): AuthState => {
  const { throwAppError } = useAppError()
  const state = React.useContext(AuthStateContext)

  if (!state)
    return throwAppError({ type: 'REACT_CONTEXT_USED_OUTSIDE_PROVIDER', name: 'AuthState' })

  return state
}
